import { toast } from 'vue-sonner'
import { useRouter } from 'vue-router'
import { useAuth } from './useAuth.js'
import { setSessionExpiredHandler } from '../../services/api/session-expiry.js'
import { versConnexionApresExpiration } from '../../utils/login-redirect.js'
import {
  isOpenToVisitors,
  isNotFoundRoute,
  isPortalPath,
  portalPrefixOf
} from '../../router/public-routes.js'

/**
 * Réaction applicative à une session expirée (401 sur un appel authentifié).
 *
 * Il n'y a plus de déconnexion sur inactivité : la session glisse à chaque
 * requête. Elle peut malgré tout finir par tomber (fenêtre de 30 jours
 * atteinte, session révoquée en base, redéploiement). Un onglet laissé ouvert
 * continue alors d'afficher l'utilisateur connecté : le premier envoi de
 * formulaire échoue sans que rien n'explique pourquoi.
 *
 * Ce composable ferme cet écart : il prévient, oublie l'utilisateur affiché et
 * renvoie vers l'écran de connexion — celui du portail où l'on se trouve, pas
 * celui du back-office. Le chemin quitté part en `redirect` pour y revenir
 * après reconnexion.
 */

/**
 * L'écran de connexion dont relève un chemin : celui de son portail, sinon
 * celui du back-office.
 *
 * La reconnaissance du portail vient de `public-routes.js` (préfixe seul ou
 * suivi d'une barre) : ce fichier en tenait une seconde liste, qui rangeait
 * `/espace-formateurXYZ` — une adresse inconnue — dans l'espace formateur.
 */
function resolveLoginPath(currentPath) {
  const prefix = portalPrefixOf(currentPath)
  return prefix ? `${prefix}/login` : '/login'
}

export function useSessionExpiryRedirect() {
  const router = useRouter()
  const { user, clearSession } = useAuth()

  // Un écran expiré déclenche souvent plusieurs appels d'un coup (une liste et
  // ses compteurs) : sans ce verrou, autant de toasts et de redirections.
  let redirecting = false

  const install = () => {
    setSessionExpiredHandler(() => {
      if (redirecting) return

      // Dans une iframe, on ne redirige JAMAIS : la page appartient au site qui
      // l'héberge (alertis.fr embarque le formulaire de devis), et y afficher
      // l'écran de connexion du back-office remplace le formulaire par un mur
      // devant un visiteur qui n'a pas de compte. Ce test ne dépend d'aucun
      // état applicatif, donc il vaut dès le premier appel.
      if (window.self !== window.top) return

      // Le chemin du NAVIGATEUR fait foi, pas celui du routeur : tant que la
      // navigation initiale n'est pas résolue, `currentRoute` vaut encore la
      // route de départ de vue-router (« / »), et un 401 reçu pendant ce
      // court instant faisait passer une page publique pour une page protégée.
      const browserPath = window.location.pathname + window.location.search
      const routerPath = router.currentRoute.value?.fullPath
      const currentPath = routerPath && routerPath !== '/' ? routerPath : browserPath

      // Page ouverte aux visiteurs (lien reçu par email, questionnaire adressé
      // à un client, formulaire embarqué) : le visiteur n'a jamais été
      // connecté, et l'envoyer vers l'écran de connexion du back-office lui
      // ferait perdre sa page pour un écran qui ne le concerne pas. L'écran
      // affiche alors son propre message, comme avant. Les deux chemins sont
      // testés : il suffit que l'un des deux désigne une page publique.
      if (isOpenToVisitors(browserPath) || isOpenToVisitors(currentPath)) return

      // Page introuvable (route attrape-tout) : son adresse est par nature
      // absente de toute liste de chemins publics — n'importe quelle URL peut
      // y mener. Sans AUCUNE session (ni back-office, ni portail), elle
      // affiche déjà son propre écran public (`usePageIntrouvable`) ; l'y
      // rediriger vers /login sur un 401 parti en arrière-plan (préférences,
      // bandeau…) remplacerait cet écran par la connexion du back-office,
      // pour un visiteur qui n'a jamais eu de compte. Connecté — back-office
      // (session encore valable au moment de la navigation) OU portail (la
      // page introuvable monte alors le cadre du portail, dont la garde
      // interne sonde elle aussi la session) —, on garde le comportement
      // normal : l'expiration reste annoncée.
      //
      // `user.value` seul ne suffit pas : il ne connaît QUE la session du
      // back-office. Un formateur (ou une entreprise, un stagiaire) dont la
      // page introuvable a détecté une session de portail active monte le
      // cadre de cet espace, qui sonde à son tour sa propre session — si elle
      // vient d'expirer entre les deux sondes, le 401 ne doit pas être avalé
      // en silence : sans ça, le formateur reste bloqué sur un cadre à moitié
      // vide, sans toast ni lien vers la connexion. On ne suppose donc « pas
      // de session » que pour un chemin qui n'est sous AUCUN préfixe de
      // portail.
      // `router.resolve`, pas `router.currentRoute` : le même 401 précoce qui
      // impose de se fier au chemin du navigateur ci-dessus (navigation
      // initiale pas encore résolue) laisserait aussi `currentRoute.value`
      // sur la route de départ, où ce n'est jamais la page introuvable —
      // `resolve` retombe sur l'attrape-tout sans dépendre d'une navigation
      // déjà terminée.
      const resolvesToNotFound = (path) => {
        try {
          return isNotFoundRoute(router.resolve(path))
        } catch {
          return false
        }
      }
      if (
        !user.value &&
        !isPortalPath(browserPath) &&
        !isPortalPath(currentPath) &&
        (resolvesToNotFound(browserPath) || resolvesToNotFound(currentPath))
      ) return

      const loginPath = resolveLoginPath(currentPath)

      // Déjà sur l'écran de connexion : rien à annoncer ni où aller.
      if (currentPath.startsWith(loginPath)) return

      redirecting = true
      clearSession()

      toast.error('Session expirée', {
        description: 'Votre session a expiré. Reconnectez-vous pour continuer.'
      })

      router
        .push(versConnexionApresExpiration(loginPath, currentPath))
        .finally(() => {
          redirecting = false
        })
    })
  }

  return { install }
}
